import React, { useState, useEffect } from 'react';
import './LoginModal.css';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBackToLogin: () => void;
  initialError?: string;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ isOpen, onClose, onBackToLogin, initialError }) => {
  const API_BASE = process.env.REACT_APP_AUTH_API_URL || 'https://api.datacircle.ru';
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(initialError || '');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (initialError) setError(initialError);
  }, [initialError]);

  useEffect(() => {
    if (!isOpen) return;
    setEmail('');
    setLoading(false);
    setSuccess('');
    setError(initialError || '');
  }, [isOpen, initialError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Введите email');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!response.ok) throw new Error('request failed');

      setSuccess('Письмо отправлено. Проверьте почту и перейдите по ссылке для сброса пароля.');
      setEmail('');
    } catch (err: any) {
      if (err.message?.includes('rate limit') || err.message?.includes('429')) {
        setError('Слишком много попыток. Попробуйте позже.');
      } else {
        setError('Не удалось отправить письмо. Проверьте email и попробуйте снова.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal__overlay" onClick={onClose}>
      <div className="auth-modal__dialog" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal__header">
          <h2 className="auth-modal__title">Восстановление пароля</h2>
          <button type="button" className="auth-modal__close" onClick={onClose} aria-label="Закрыть">×</button>
        </div>

        <div className="auth-modal__body">
          <p className="auth-modal__subtitle">
            Введите email, и мы отправим ссылку для сброса пароля
          </p>

          {error && <div className="auth-modal__error">{error}</div>}
          {success && <div className="auth-modal__success">{success}</div>}

          {!success && (
            <form onSubmit={handleSubmit} className="auth-modal__form">
              <div className="auth-modal__field">
                <label htmlFor="forgot-email" className="auth-modal__label">Email</label>
                <input
                  type="email"
                  id="forgot-email"
                  className="auth-modal__input"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="name@company.com"
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              <button type="submit" className="auth-modal__submit" disabled={loading}>
                {loading ? 'Отправляем…' : 'Восстановить пароль'}
              </button>
            </form>
          )}

          <div className="auth-modal__links">
            <p>
              <button type="button" className="auth-modal__link" onClick={onBackToLogin} disabled={loading}>
                ← Вернуться ко входу
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
